import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Check, ArrowRight, Zap, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { BUYERS } from "@/lib/biohackerBrand";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Solutions — buyer-based navigation. Ten dedicated sections (anchor-linked)
 * from the homepage teaser tiles.
 *
 * Each section now surfaces two CTAs:
 *   1. "Request tailored demo" — existing lead form for sales.
 *   2. "Instant demo access"   — opens the persona/sport picker and one-clicks
 *      the caller into the pre-seeded @biohacker.demo account.
 */
export default function Solutions() {
  const { hash } = useLocation();
  const [pickerFor, setPickerFor] = useState(null); // BUYER row currently opening picker
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-[#050810] text-white" data-testid="solutions-page">
      <SiteNav />
      <section className="pt-32 pb-14 border-b border-white/8 max-w-7xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">Solutions</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]">
          Built for every decision-maker.<br/>
          <span className="text-white/50">Athletes, coaches, trainers, directors, medicine, and beyond.</span>
        </h1>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <span className="text-xs text-white/45">Every demo is registration-gated — we'll email you credentials so you can return any time.</span>
        </div>
        <div className="mt-6 flex flex-wrap gap-2" data-testid="solutions-toc">
          {BUYERS.map((b) => (
            <a key={b.id} href={`#${b.id}`}
              className="text-[10px] tracking-[0.2em] uppercase font-bold px-3 py-1.5 rounded-full border border-white/15 text-white/60 hover:text-white hover:border-white/30 transition-colors"
              data-testid={`toc-${b.id}`}>
              {b.label}
            </a>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-14 space-y-10">
        {BUYERS.map((b) => (
          <div key={b.id} id={b.id} className="ink-card rounded-2xl p-8" data-testid={`solution-section-${b.id}`}>
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 mb-2">Solution</div>
                <div className="font-display font-black text-2xl text-white leading-tight">{b.label}</div>
                <div className="mt-2 h-1 w-12 rounded-full" style={{ background: b.tone }} />
              </div>
              <div className="md:col-span-2">
                <p className="text-white/75 leading-relaxed">{b.lead}</p>
                <ul className="mt-5 grid sm:grid-cols-2 gap-2">
                  {b.bullets.map((line) => (
                    <li key={line} className="flex items-start gap-2 text-sm text-white/85">
                      <Check className="h-4 w-4 mt-0.5" style={{ color: b.tone }} /> {line}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/request-demo" data-testid={`solution-cta-${b.id}`}>
                    <Button className="text-white font-bold text-sm" style={{ backgroundColor: b.tone }}>
                      Request tailored demo <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  {b.case_href && (
                    <Link to={b.case_href} data-testid={`solution-case-${b.id}`}>
                      <Button variant="ghost" className="text-white/90 border border-white/25 hover:bg-white/10 font-bold text-sm">
                        See the full case <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => setPickerFor(b)}
                    className="text-white/90 border border-white/25 hover:bg-white/10 font-bold text-sm"
                    data-testid={`solution-instant-${b.id}`}
                  >
                    <Zap className="h-4 w-4 mr-2 text-fuchsia-300" /> Instant demo access
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      <InstantDemoPicker
        buyer={pickerFor}
        onClose={() => setPickerFor(null)}
      />

      <SiteFooter />
    </div>
  );
}


// ── Instant demo picker ────────────────────────────────────────────────
// Modal that appears when a user taps "Instant demo access" on a Solution
// card. For sport-scoped personas it asks which sport; for the three
// agnostic personas it fires immediately. Redirects into the appropriate
// dashboard once the backend sets the auth cookies.
const SPORT_CHOICES = [
  { id: "softball",   label: "Softball" },
  { id: "football",   label: "Football" },
  { id: "basketball", label: "Basketball" },
  { id: "soccer",     label: "Soccer" },
  { id: "volleyball", label: "Volleyball" },
  { id: "track",      label: "Track & Field" },
  { id: "olympic",    label: "Olympic Sports" },
];
const AGNOSTIC_IDS = new Set(["pro", "military", "facility"]);

function InstantDemoPicker({ buyer, onClose }) {
  const navigate = useNavigate();
  const [sport, setSport] = useState("");
  const [form, setForm] = useState({ name: "", email: "", role: "", org: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (buyer) {
      setSport("");
      setForm({ name: "", email: "", role: buyer.label || "", org: "" });
      setBusy(false);
    }
  }, [buyer]);

  if (!buyer) return null;
  const isAgnostic = AGNOSTIC_IDS.has(buyer.id);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const launch = async () => {
    if (!isAgnostic && !sport) { toast.error("Pick a sport first"); return; }
    if (!form.name.trim() || !form.email.trim() || !form.role.trim()) {
      toast.error("Name, email, and role are required"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/demo/instant", {
        persona: buyer.id,
        sport: isAgnostic ? null : sport,
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role.trim(),
        org: form.org.trim() || null,
      });
      toast.success("Demo credentials emailed", { description: `We'll also sign you into ${data.name}` });
      onClose();
      navigate(data.redirect || "/dashboard/coach");
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 py-6"
      role="dialog"
      aria-modal="true"
      data-testid="solutions-instant-picker"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-lg rounded-2xl bg-[#0B1220] border border-white/10 text-white shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between px-6 py-5 border-b border-white/8 sticky top-0 bg-[#0B1220] z-10">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-fuchsia-300/80">Instant demo access</div>
            <div className="font-display font-black text-xl mt-1">{buyer.label}</div>
            <div className="text-xs text-white/50 mt-1">Register once — we'll email your credentials and sign you in.</div>
          </div>
          <button onClick={onClose} data-testid="solutions-instant-close"
            className="p-2 rounded-md text-white/60 hover:text-white hover:bg-white/5 border border-white/10">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          {!isAgnostic && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold mb-2">1 · Pick a sport</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="solutions-instant-sports">
                {SPORT_CHOICES.map((s) => {
                  const active = sport === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSport(s.id)}
                      data-testid={`solutions-instant-sport-${s.id}`}
                      className={`px-3 py-2 rounded-lg border text-sm font-semibold transition ${active ? "text-white border-transparent" : "text-white/75 border-white/15 hover:border-white/35"}`}
                      style={active ? { backgroundColor: buyer.tone } : {}}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">
              {isAgnostic ? "1 · Register" : "2 · Register"}
            </div>
            <input
              type="text"
              value={form.name}
              onChange={set("name")}
              placeholder="Full name *"
              className="w-full rounded-md border border-white/12 bg-white/[0.04] px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30"
              data-testid="solutions-instant-name"
            />
            <input
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="Work email *"
              className="w-full rounded-md border border-white/12 bg-white/[0.04] px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30"
              data-testid="solutions-instant-email"
            />
            <input
              type="text"
              value={form.role}
              onChange={set("role")}
              placeholder="Your role / title *"
              className="w-full rounded-md border border-white/12 bg-white/[0.04] px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30"
              data-testid="solutions-instant-role"
            />
            <input
              type="text"
              value={form.org}
              onChange={set("org")}
              placeholder="Organization (optional)"
              className="w-full rounded-md border border-white/12 bg-white/[0.04] px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30"
              data-testid="solutions-instant-org"
            />
            <div className="text-[11px] text-white/40">
              We'll email your demo credentials so you can log back in later. No credit card required.
            </div>
          </div>

          <Button
            onClick={launch}
            disabled={busy || (!isAgnostic && !sport) || !form.name || !form.email || !form.role}
            className="w-full text-white font-bold text-sm py-5"
            style={{ backgroundColor: buyer.tone }}
            data-testid="solutions-instant-go"
          >
            {busy
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Registering & signing you in…</>
              : <>Register + launch demo <ArrowRight className="ml-2 h-4 w-4" /></>}
          </Button>
        </div>
      </div>
    </div>
  );
}
